import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CollectionResource, ItemResource, Link, MimeTypeEnum, PagedResource } from '@ngx-hal/models';
import { map, Observable } from 'rxjs';

interface TcTyresLookupResponse {
  page?: {
    totalElements?: number;
  };
  _embedded?: {
    tcTyres?: unknown[];
  };
}

export class ItemResourceWebService<Entity extends ItemResource> {
  private targets = [
    'twAdditionalInfoes',
    'motorBikeSegments',
    'carSegments',
    'carTypes',
    'motorBikeTypes',
    'cars',
    'motorBikes',
    'tcExceptionTables',
  ];
  private readonly statusFallbackTargets = ['motorBikeHomologations'];
  constructor(private readonly httpClient: HttpClient) {}

  getAll(link: Link): Observable<CollectionResource<Entity>> {
    // 405 ==> Not allowed

    // application/hal+json
    // application/json

    throw `Not yet implemented: cannot query all from '${JSON.stringify(link)}'`;
  }

  getBy(link: Link, filter: HttpParams): Observable<PagedResource<Entity>> {
    // 405 ==> Not allowed

    // application/hal+json
    // application/json

    return this.httpClient.get<PagedResource<Entity>>(link.href, {
      params: filter,
      headers: { Accept: MimeTypeEnum.HAL },
    });
  }

  get(link: Link): Observable<any> {
    return this.httpClient.get<PagedResource<Entity>>(link.href, {
      headers: { Accept: MimeTypeEnum.Json },
    });
  }

  getAssociatedTyreCount(
    productId: string | number,
    resourcePath: 'products' | 'dimensions' = 'products',
  ): Observable<number> {
    const filterKey = resourcePath === 'dimensions' ? 'dimension.id' : 'product.id';
    const filter = new HttpParams().set(filterKey, String(productId));

    return this.getBy({ href: '/vehicles/api/tcTcTyres' }, filter).pipe(
      map(response => this.extractTyreCountFromTcTyresResponse(response as unknown as TcTyresLookupResponse)),
    );
  }

  private extractTyreCountFromTcTyresResponse(response?: TcTyresLookupResponse | null): number {
    const total = Number(response?.page?.totalElements);

    return Number.isFinite(total) ? total : response?._embedded?.tcTyres?.length ?? 0;
  }

  private normalizeDeprecatedTcProductLink(link: Link, operation: 'PATCH' | 'PUT' | 'DELETE'): Link {
    const href = link?.href ?? '';
    const matched = href.match(/^(https?:\/\/[^/]+)?\/api\/tyre\/tc\/products\/([^/?#]+)(\/?.*)?$/i);

    if (!matched) return link;

    const origin = matched[1] || `${document.location.protocol}//${document.location.host}`;

    const normalizedHref = `${origin}/api/tcTyreCategories/${matched[2]}${matched[3] ?? ''}`;

    console.warn('[DeprecatedEndpoint] Rewriting deprecated TC product item URL.', {
      operation,
      oldUrl: href,
      newUrl: normalizedHref,
    });

    return {
      ...link,
      href: normalizedHref,
    };
  }

  create(link: Link, entity: Entity): Observable<Entity> {
    // 201 ==> Created
    // 405 ==> Not allowed

    // application/hal+json
    // application/json

    if (this.targets.some(segment => link.href.includes(segment))) {
      entity = this.replaceUndefinedWithNullShallow(entity as Partial<Entity>) as Entity;
    }

    if (this.statusFallbackTargets.some(segment => link.href.includes(segment))) {
      entity = this.applyDefaultStatusFallback(
        entity as unknown as Record<string, unknown>,
      ) as Entity;
    }

    return this.httpClient.post<Entity>(link.href, entity, {
      headers: { Accept: MimeTypeEnum.HAL },
    });
  }

  read(link: Link): Observable<Entity> {
    // 200 ==> OK
    // 405 ==> Not allowed

    // application/hal+json
    // application/json

    return this.httpClient.get<Entity>(link.href, {
      headers: { Accept: MimeTypeEnum.HAL },
    });
  }

  update(link: Link, entity: Entity): Observable<Entity> {
    // 200 ==> OK
    // 201 ==> Created
    // 204 ==> No content
    // 405 ==> Not allowed

    // application/hal+json
    // application/json

    throw `Not yet implemented: cannot update '${JSON.stringify(link)}' with ${JSON.stringify(entity)}`;
  }

  delete(link: Link): Observable<void> {
    // 204 ==> No content
    // 405 ==> Not allowed
    const normalizedLink = this.normalizeDeprecatedTcProductLink(link, 'DELETE');
    return this.httpClient.delete<void>(normalizedLink.href, {
      headers: { Accept: MimeTypeEnum.HAL },
    });
  }

  head(link: Link): Observable<HttpHeaders> {
    throw `Not yet implemented: cannot head '${JSON.stringify(link)}'`;
  }

  // When using Angular's HttpClient to make a PATCH request, parameters with undefined values are not sent by default.
  // This behavior is intentional and aligns with how HttpParams handles undefined values.
  // To include parameters with undefined values in your PATCH request
  // Converting undefined values to null
  replaceUndefinedWithNullShallow<T extends Record<string, unknown>>(obj: T): T {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, value === undefined ? null : value]),
    ) as T;
  }

  // Ensures status has a backend-safe default when the UI leaves it empty.
  // Called only for resources listed in `statusFallbackTargets`.
  private applyDefaultStatusFallback<T extends Record<string, unknown>>(entity: T): T {
    const statusValue = entity['status'];
    const isStatusEmpty =
      statusValue === null ||
      statusValue === undefined ||
      (typeof statusValue === 'string' &&
        (statusValue.trim().length === 0 || statusValue.trim().toLowerCase() === 'null'));

    if (isStatusEmpty) {
      return { ...entity, status: 'Active' } as T;
    }

    return entity;
  }

  patch(link: Link, entity: Partial<Entity>): Observable<Entity> {
    // 204 ==> No content

    // application/hal+json
    // application/json
    // application/patch+json
    // application/merge-patch+json

    // We will replace undefined values with null values for all the entity
    // if it is requires
    if (this.targets.some(segment => link.href.includes(segment))) {
      entity = this.replaceUndefinedWithNullShallow(entity as Partial<Entity>) as Entity;
    }

    if (this.statusFallbackTargets.some(segment => link.href.includes(segment))) {
      entity = this.applyDefaultStatusFallback(
        entity as unknown as Record<string, unknown>,
      ) as Partial<Entity>;
    }

    const normalizedLink = this.normalizeDeprecatedTcProductLink(link, 'PATCH');

    return this.httpClient.patch<Entity>(normalizedLink.href, entity, {
      headers: { Accept: MimeTypeEnum.HAL },
    });
  }

  put(link: Link, entity: Partial<Entity>): Observable<Entity> {
    // 204 ==> No content

    // application/hal+json
    // application/json
    // application/patch+json
    // application/merge-patch+json

    const normalizedLink = this.normalizeDeprecatedTcProductLink(link, 'PUT');

    return this.httpClient.put<Entity>(normalizedLink.href, entity, {
      headers: { Accept: MimeTypeEnum.HAL },
    });
  }

  post(link: Link, data: { originalCarId: number; duplicateCarId: number }): Observable<any> {
    return this.httpClient.post(
      `${link.href}?originalCarId=${data.originalCarId}&duplicateCarId=${data.duplicateCarId}`,
      {},
    );
  }

  motorBikePost(link: Link, data: { originalMotorBikeId: number; duplicateMotorBikeId: number }): Observable<any> {
    return this.httpClient.post(
      `${link.href}?originalMotorBikeId=${data.originalMotorBikeId}&duplicateMotorBikeId=${data.duplicateMotorBikeId}`,
      {},
    );
  }

  fvmPost(link: Link, data: { date1: string; date2: string; productLine: string }): Observable<any> {
    return this.httpClient.post<Entity>(link.href, data);
  }

  mapRequestPost(link: Link, userId: number, requestId: number): Observable<any> {
    return this.httpClient.post(
      `${link.href}?userId=${userId}&requestId=${requestId}`,
      {},
    );
  }
}
